import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BasePage } from '../BasePage';
import Pagination from '../../table/Pagination';
import Table from '../../table/Table';
import NotificationDetail from './components/NotificationDetail';
import DateRangePicker from './components/DateRangePicker';
import { NotificationLevel } from '../../enums';
import NotificationCheckbox from './components/NotificationCheckbox';
import { format } from '../../../utils/formatting';
import LoadingSpinner from '../../loadingSpinner/LoadingSpinner';

import { useOrderAndPaginationControl } from '../../table/hooks';
import { useNotificationQueryGenerator, useFiltrationControl } from './utils';
import MeasuringPointSelect from '../../graph/components/MeasuringPointSelect';
import SearchField from './components/SearchField';

const NotificationsPage = () => {
    const { t } = useTranslation();
    const orderAndPaginationControl = useOrderAndPaginationControl();
    const filtrationControl = useFiltrationControl();
    const {
        filters,
        setLevelsFilter,
        setStartDateFilter,
        setEndDateFilter,
        setMeasuringPointFilter,
        setSearchFilter,
    } = filtrationControl;
    const { notifications, loading, updating } = useNotificationQueryGenerator(
        orderAndPaginationControl,
        filtrationControl
    );

    const columns = useMemo(
        () => [
            {
                key: 'title',
                title: t('Notifications'),
                showSortIcon: false,
                render: (_, item) => <NotificationDetail {...item} key={item.id} />,
            },
            {
                key: 'createdAt',
                title: gettext('Date/Time'),
                showSortIcon: true,
                render: (_, { createdAt }) => (
                    <p className="pr-4" data-datetime={createdAt} data-datetime-target="text">
                        {format(createdAt, { useBrowserTimezone: true })}
                    </p>
                ),
            },
        ],
        [t]
    );

    const title = t('Notifications');
    const nodes = useMemo(() => notifications.map((edge) => edge.node), [notifications]);

    return (
        <BasePage title={title}>
            <div className="main-container-new mb-14 pb-4 pt-2">
                <div className="align-center flex w-full justify-between">
                    <div className="my-4 flex w-1/2">
                        {Object.values(NotificationLevel).map((level, idx) => (
                            <NotificationCheckbox
                                key={idx}
                                level={level}
                                isChecked={filters.levels[level] ?? false}
                                onChange={() => setLevelsFilter(level, !filters.levels[level])}
                            />
                        ))}
                    </div>
                    <DateRangePicker
                        startDate={filters.startDate}
                        endDate={filters.endDate}
                        onStartDateChange={(value) => setStartDateFilter(value)}
                        onEndDateChange={(value) => setEndDateFilter(value)}
                    />
                </div>
                <div className="mb-4 w-1/2">
                    <MeasuringPointSelect
                        className=""
                        selectId="mp-selector-notifications-page"
                        onSelectChange={setMeasuringPointFilter}
                        initialValue={filters.mpPk}
                        allowNoSelection={true}
                    />
                </div>
                <div className="mb-14">
                    <div className="align-center mb-4 flex w-full justify-between">
                        <div className="w-1/2">
                            <SearchField value={filters.search} onChange={setSearchFilter} />
                        </div>
                        <span className="space-x-2">
                            {updating && (
                                <LoadingSpinner className="h-5 align-middle text-gray-400" />
                            )}
                            {orderAndPaginationControl.pageCount > 1 && (
                                <Pagination orderAndPaginationControl={orderAndPaginationControl} />
                            )}
                        </span>
                    </div>
                    {loading || updating || nodes.length > 0 ? (
                        <Table
                            data={nodes}
                            columns={columns}
                            header={title}
                            loading={loading || updating}
                            orderAndPaginationControl={orderAndPaginationControl}
                            showBorder={false}
                            showDivider={false}
                            wrapperClasses="bg-white"
                            loaderClasses=""
                            headerRowClasses="border-2"
                            headerCellClasses="p-4"
                            rowClasses="py-4 border-2 hover:bg-main-background hover:cursor-pointer"
                            rowCellClasses=""
                        />
                    ) : (
                        <p className="mt-40 text-center">{gettext('No data found')}</p>
                    )}
                </div>
            </div>
        </BasePage>
    );
};

export default NotificationsPage;
